import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getOrganizationId } from 'store';

const MAX_SCREENSHOTS = 5;

/**
 * `prefill` ({requestType, description, chatMessages}) comes from Verstaani avustaja when it
 * could not answer: the form opens filled in and the chat goes along to customer service, so
 * the user never explains the same thing twice. Null opens the plain form.
 */
function SupportRequestDialog({ open, onClose, prefill = null }) {
  const { t } = useTranslation();
  const [requestType, setRequestType] = useState('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && prefill) {
      setRequestType(prefill.requestType || 'help');
      setDescription(prefill.description || '');
    }
    // Applied once per opening; the user edits freely after that
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const chatMessages = prefill?.chatMessages ?? [];

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.slice(0, MAX_SCREENSHOTS - screenshots.length);
    const filesWithPreview = newFiles.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file)
      })
    );
    setScreenshots(prev => [...prev, ...filesWithPreview].slice(0, MAX_SCREENSHOTS));
  }, [screenshots.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxFiles: MAX_SCREENSHOTS - screenshots.length,
    disabled: screenshots.length >= MAX_SCREENSHOTS,
  });

  const removeScreenshot = (index) => {
    setScreenshots(prev => {
      const newScreenshots = [...prev];
      URL.revokeObjectURL(newScreenshots[index].preview);
      newScreenshots.splice(index, 1);
      return newScreenshots;
    });
  };

  const resetForm = () => {
    setRequestType('');
    setDescription('');
    screenshots.forEach(file => URL.revokeObjectURL(file.preview));
    setScreenshots([]);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!requestType || !description.trim()) {
      setError(t('Please fill in all required fields'));
      return;
    }

    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('request_type', requestType);
    formData.append('description', description);

    const orgId = getOrganizationId();
    if (orgId) {
      formData.append('organization_id', orgId);
    }

    screenshots.forEach((file) => {
      formData.append('screenshots', file);
    });

    if (chatMessages.length > 0) {
      formData.append('chat_messages', JSON.stringify(chatMessages));
    }

    try {
      await axios.post('/api/support/request/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to submit support request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('Submit support request')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('Support request submitted successfully!')}
          </Alert>
        )}

        {prefill && !success && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="support-request-prefilled">
            {t('helpChatbot.prefilledNote')}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="request-type-label">{t('Request type')}</InputLabel>
          <Select
            labelId="request-type-label"
            value={requestType}
            label={t('Request type')}
            onChange={(e) => setRequestType(e.target.value)}
            disabled={submitting || success}
          >
            <MenuItem value="bug">{t('Bug report')}</MenuItem>
            <MenuItem value="feature">{t('Feature request')}</MenuItem>
            <MenuItem value="help">{t('Help with usage')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('Description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('Describe your issue or request...')}
          disabled={submitting || success}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('Add screenshot that explains your problem')} ({screenshots.length}/{MAX_SCREENSHOTS})
        </Typography>

        {screenshots.length < MAX_SCREENSHOTS && !success && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              mb: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {isDragActive
                ? t('Drop the files here...')
                : t('Drag and drop screenshots here, or click to select')}
            </Typography>
          </Box>
        )}

        {screenshots.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {screenshots.map((file, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <img
                  src={file.preview}
                  alt={`Screenshot ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {!success && (
                  <IconButton
                    size="small"
                    onClick={() => removeScreenshot(index)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      },
                      padding: 0.5,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || success || !requestType || !description.trim()}
        >
          {submitting ? <CircularProgress size={24} /> : t('Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SupportRequestDialog;
